import { useState, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/constants/icons';
import { CanvaChannel } from '@/types/canva';
import { useUpdateCanvaLabel } from '@/hooks/useCanvaImport';

/**
 * CanvaAccountSelector Component
 *
 * Step 1: Choose which Canva channel to use for browsing designs.
 * Each channel displays an inline rename (label) affordance using the pencil icon
 * (hm-pencil, mapped to ICONS.EDIT which is 'pencil' — verified in happyicon.css).
 *
 * Name display priority per channel:
 *   1. metadata.label   (user-set friendly name)
 *   2. metadata.displayName  (Canva account display name)
 *   3. platformUsername  (OAuth username)
 *   4. 'Canva account'  (final fallback)
 *
 * Discriminator hint (shown when no label is set):
 *   - Shows "Team <first8chars>…" when canvaTeamId is available
 *   - Falls back to "User <first8chars>…" from canvaUserId
 *   - Helps distinguish two channels for the same Canva user on different teams
 */

interface CanvaAccountSelectorProps {
  channels: CanvaChannel[];
  selectedChannelId: number | null;
  onSelect: (channelId: number) => void;
  isLoading: boolean;
  /** Optional: trigger the Canva OAuth reconnect flow. Shown in the empty state
   *  when provided, so a revoked/disconnected Canva channel prompts reconnect
   *  instead of silently offering nothing. */
  onReconnect?: () => void;
  /** True when a Canva channel exists but none are active (e.g. token revoked) —
   *  shows reconnect-specific copy in the empty state instead of the
   *  "never connected" copy. */
  needsReconnect?: boolean;
}

// ---------------------------------------------------------------------------
// Helper: derive the display name from channel data (priority order)
// ---------------------------------------------------------------------------
function getChannelDisplayName(channel: CanvaChannel): string {
  return (
    channel.metadata?.label ||
    channel.metadata?.displayName ||
    channel.platformUsername ||
    'Canva account'
  );
}

// ---------------------------------------------------------------------------
// Helper: build a short discriminator hint when no label is set
// ---------------------------------------------------------------------------
function getDiscriminatorHint(channel: CanvaChannel): string | null {
  // No need for a hint if the user already set a label
  if (channel.metadata?.label) return null;

  const teamId = channel.metadata?.canvaTeamId;
  if (teamId && typeof teamId === 'string' && teamId.length > 0) {
    return `Team ${teamId.slice(0, 8)}${teamId.length > 8 ? '…' : ''}`;
  }

  const userId = channel.metadata?.canvaUserId;
  if (userId && typeof userId === 'string' && userId.length > 0) {
    return `User ${userId.slice(0, 8)}${userId.length > 8 ? '…' : ''}`;
  }

  return null;
}

// ---------------------------------------------------------------------------
// InlineRenameField — revealed when user clicks the pencil icon
// ---------------------------------------------------------------------------
interface InlineRenameFieldProps {
  channelId: number;
  currentLabel: string;
  onDone: () => void;
}

const InlineRenameField = ({ channelId, currentLabel, onDone }: InlineRenameFieldProps) => {
  const [value, setValue] = useState(currentLabel);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateLabel = useUpdateCanvaLabel();

  const save = () => {
    // Guard against double-fire: the confirm button's onClick and the input's onBlur
    // both call save(), so if the mutation is already in flight we bail out immediately.
    if (updateLabel.isPending) return;
    const trimmed = value.trim();
    if (trimmed === currentLabel) {
      onDone();
      return;
    }
    updateLabel.mutate(
      { channelId, label: trimmed },
      { onSettled: () => onDone() }
    );
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onDone();
    }
  };

  return (
    <div
      className="flex items-center gap-1.5 mt-1"
      // Prevent click on the form from bubbling up and triggering onSelect
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        autoFocus
        type="text"
        value={value}
        maxLength={100}
        disabled={updateLabel.isPending}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={save}
        placeholder="Add a label…"
        className="
          flex-1 min-w-0 text-sm px-2 py-1 rounded border border-gray-300 bg-white
          text-gray-900 placeholder-gray-400
          focus:outline-none focus:border-gray-500
          disabled:opacity-50 disabled:cursor-not-allowed
        "
        aria-label="Channel label"
      />
      {/* Confirm button — hm-tick verified in happyicon.css */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); save(); }}
        disabled={updateLabel.isPending}
        className="
          flex-shrink-0 p-1 rounded text-gray-500
          hover:text-gray-800 hover:bg-gray-100
          disabled:opacity-40 disabled:cursor-not-allowed
          focus:outline-none focus:ring-0
        "
        aria-label="Save label"
      >
        <Icon name={ICONS.CHECK} size="sm" ariaHidden />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const CanvaAccountSelector = ({
  channels,
  selectedChannelId,
  onSelect,
  isLoading,
  onReconnect,
  needsReconnect = false,
}: CanvaAccountSelectorProps) => {
  const { t } = useTranslation('common');
  // Track which channel has its rename field open (null = none)
  const [renamingChannelId, setRenamingChannelId] = useState<number | null>(null);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 bg-gray-100 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  // Empty state
  if (channels.length === 0) {
    return (
      <div className="text-center py-12">
        <img src="/assets/img/canva-circle.png" alt="" className="mx-auto w-16 h-16 mb-4 rounded-full" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {needsReconnect
            ? t('canva_needs_reconnect_title', { defaultValue: 'Reconnect Canva to keep importing' })
            : t('no_canva_accounts')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {needsReconnect
            ? t('canva_needs_reconnect_description', {
                defaultValue: 'Your Canva connection expired. Reconnect to import your designs.',
              })
            : t('connect_canva_account_first')}
        </p>
        {onReconnect && (
          <Button variant="primary" onClick={onReconnect} className="gap-2">
            <Icon name={ICONS.REFRESH} size="sm" ariaHidden />
            {t('canva_reconnect_action')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {t('select_canva_account_to_browse')}
      </p>

      <div className="space-y-2">
        {channels.map((channel) => {
          const isSelected = selectedChannelId === channel.id;
          const isRenaming = renamingChannelId === channel.id;
          const displayName = getChannelDisplayName(channel);
          const hint = getDiscriminatorHint(channel);
          const currentLabel = channel.metadata?.label ?? '';

          return (
            <div
              key={channel.id}
              className={`
                w-full p-4 rounded-lg border-2 transition-all duration-200
                flex items-start gap-4
                ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50'
                }
              `}
            >
              {/* Clicking the row body selects the account */}
              <button
                type="button"
                className="flex items-start gap-4 flex-1 min-w-0 text-left focus:outline-none focus:ring-0"
                onClick={() => onSelect(channel.id)}
                aria-pressed={isSelected}
              >
                {/* Selection indicator */}
                <div
                  className={`
                    mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                    ${
                      isSelected
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300 bg-white'
                    }
                  `}
                >
                  {isSelected && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>

                {/* Channel info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon
                      name={ICONS.CLOUD}
                      size="sm"
                      color={isSelected ? 'text-primary-600' : 'text-gray-400'}
                      ariaHidden
                    />
                    <span className="font-medium text-gray-900 truncate">
                      {displayName}
                    </span>
                  </div>

                  {/* Discriminator hint — shown when no label is set and there is a team/user slug */}
                  {hint && !isRenaming && (
                    <p className="text-xs text-gray-400 mt-0.5 font-mono">
                      {hint}
                    </p>
                  )}

                  {/* Inline rename field */}
                  {isRenaming && (
                    <InlineRenameField
                      channelId={channel.id}
                      currentLabel={currentLabel}
                      onDone={() => setRenamingChannelId(null)}
                    />
                  )}
                </div>
              </button>

              {/* Right-side controls: rename icon + active badge */}
              <div
                className="flex items-center gap-2 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Active badge */}
                {channel.isActive && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                    {t('active')}
                  </span>
                )}

                {/* Rename/pencil icon — hm-pencil is ICONS.EDIT, verified in happyicon.css */}
                {!isRenaming && (
                  <button
                    type="button"
                    onClick={() => setRenamingChannelId(channel.id)}
                    className="
                      p-1.5 rounded text-gray-400
                      hover:text-gray-700 hover:bg-gray-100
                      focus:outline-none focus:ring-0
                    "
                    aria-label={`Rename ${displayName}`}
                    title="Edit label"
                  >
                    {/* ICONS.EDIT = 'pencil' → class hm hm-pencil, confirmed in happyicon.css */}
                    <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
